import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'
import { TeamType } from '../../types'

interface TestspielSlot {
  slot_id: string
  slot_date: string
  slot_time: string
  slot_status: 'available' | 'pending' | 'confirmed' | 'cancelled'
  team_value?: string | null
  opponent?: string | null
  trainer_name?: string | null
}

// Team time slot rules for validation
const getValidTimesForTeam = (team: TeamType, date: string): string[] => {
  const dayOfWeek = new Date(date + 'T00:00:00').getDay() // 0 = Sunday, 6 = Saturday
  
  // Active teams (1., 2., 3. Mannschaft, Damen): Saturday 15:00, 17:30 and Sunday 15:00
  if (['1. Mannschaft', '2. Mannschaft', '3. Mannschaft', 'Damen'].includes(team)) {
    if (dayOfWeek === 6) return ['15:00', '17:30'] // Saturday
    if (dayOfWeek === 0) return ['15:00'] // Sunday
    return []
  }
  
  // Seniors (50+, 40+, 30+): Saturday 20:00
  if (['50+', '40+', '30+'].includes(team)) {
    if (dayOfWeek === 6) return ['20:00'] // Saturday
    return []
  }
  
  // B- and C-Juniors (BA, BB, CA, CB): Saturday 12:30
  if (['BA', 'BB', 'CA', 'CB'].includes(team)) {
    if (dayOfWeek === 6) return ['12:30'] // Saturday
    return []
  }
  
  // D-Juniors (D9A, D9B, D9C, D7A, D7B, D7C, FF-17): Saturday 10:00 and Sunday 12:30
  if (['D9A', 'D9B', 'D9C', 'D7A', 'D7B', 'D7C', 'FF-17'].includes(team)) {
    if (dayOfWeek === 6) return ['10:00'] // Saturday
    if (dayOfWeek === 0) return ['12:30'] // Sunday
    return []
  }
  
  // E-Juniors, FF-14, F-Juniors, G: Sunday 10:00
  if (['EA', 'EB', 'EC', 'ED', 'FF-14', 'FA', 'FB', 'G'].includes(team)) {
    if (dayOfWeek === 0) return ['10:00'] // Sunday
    return []
  }
  
  return []
}

// Status display mapping
const getStatusDisplay = (status: string): string => {
  switch (status) {
    case 'available': return 'frei'
    case 'pending': return 'angefragt'
    case 'confirmed': return 'besetzt'
    case 'cancelled': return 'frei'
    default: return 'frei'
  }
}

interface SupabaseTestspielDatesStore {
  teamSlots: TestspielSlot[]
  allSlots: TestspielSlot[]
  loading: boolean
  error: string | null
  
  // Actions  
  fetchTeamSlots: (team: TeamType) => Promise<void>
  fetchAllSlots: () => Promise<void>
  getDatesForTeam: (team: TeamType) => string[]
  getDateTimeSlotStatus: (team: TeamType, date: string, time: string) => 'available' | 'pending' | 'confirmed'
  getStatusDisplay: (status: string) => string
  isValidTimeForTeam: (team: TeamType, date: string, time: string) => boolean
  getValidTimesForTeam: (team: TeamType, date: string) => string[]
  
  // Legacy compatibility
  testspielDates: Record<TeamType, string[]>
  fetchTestspielDates: () => Promise<void>
  addDateTimeSlot: (date: string, time: string, notes?: string) => Promise<{ error?: string }>
  removeDateTimeSlot: (id: string) => Promise<{ error?: string }>
}

export const useSupabaseTestspielDatesStore = create<SupabaseTestspielDatesStore>((set, get) => ({
  teamSlots: [],
  allSlots: [],
  loading: false,
  error: null,
  testspielDates: {} as Record<TeamType, string[]>,

  fetchTeamSlots: async (team) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .rpc('get_team_slots', { team_name: team })

      if (error) throw error

      set({ teamSlots: data || [], loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  fetchAllSlots: async () => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .rpc('get_all_slots')

      if (error) throw error

      set({ allSlots: data || [], loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  isValidTimeForTeam: (team, date, time) => {
    const validTimes = getValidTimesForTeam(team, date)
    return validTimes.includes(time)
  },

  getValidTimesForTeam: (team, date) => {
    // Get the theoretical valid times for this team on this date
    const theoreticalTimes = getValidTimesForTeam(team, date)
    
    // Filter out times that are already taken by other teams
    const availableTimes = theoreticalTimes.filter(time => {
      const slot = get().teamSlots.find(s => 
        s.slot_date === date && s.slot_time === time
      )
      
      if (!slot) return true // No slot found = available
      
      // Available if: slot is free OR slot belongs to this team (so they can edit)
      return (slot.team_value === null && slot.slot_status === 'available') ||
             slot.team_value === team
    })
    
    return availableTimes
  },

  getDateTimeSlotStatus: (team, date, time) => {
    // First check if this is a valid combination for the team
    if (!get().isValidTimeForTeam(team, date, time)) {
      return 'confirmed' // Invalid combinations show as unavailable (red)
    }
    
    // Find the slot in teamSlots
    const slot = get().teamSlots.find(s => 
      s.slot_date === date && s.slot_time === time
    )
    
    if (!slot) return 'available'
    
    // If slot has no team assigned and is available, it's free
    if (slot.team_value === null && slot.slot_status === 'available') {
      return 'available'
    }
    
    // If slot is taken by another team, it's confirmed (unavailable)
    if (slot.team_value !== null && slot.team_value !== team) {
      return 'confirmed'
    }
    
    // If slot is for this team, return its actual status
    if (slot.team_value === team) {
      return slot.slot_status as 'available' | 'pending' | 'confirmed'
    }
    
    return slot.slot_status as 'available' | 'pending' | 'confirmed'
  },

  getStatusDisplay: (status: string) => {
    return getStatusDisplay(status)
  },

  getDatesForTeam: (team) => {
    // Extract unique dates from teamSlots for this team
    const slots = get().teamSlots
    const validTimes = (date: string) => getValidTimesForTeam(team, date)
    
    const dates = Array.from(new Set(
      slots
        .filter(slot => {
          // Only show slots that are available or pending (not confirmed by other teams)
          const isAvailableSlot = slot.slot_status === 'available' && slot.team_value === null
          const isPendingForTeam = slot.slot_status === 'pending' && slot.team_value === team
          const hasValidTime = validTimes(slot.slot_date).includes(slot.slot_time)
          
          return (isAvailableSlot || isPendingForTeam) && hasValidTime
        })
        .map(slot => slot.slot_date)
    )).sort()
    
    return dates
  },

  // Legacy methods for backward compatibility
  fetchTestspielDates: async () => {
    // This is now handled by fetchTeamSlots for each team
    set({ testspielDates: {} as Record<TeamType, string[]> })
  },

  addDateTimeSlot: async (date, time, notes) => {
    try {
      const { profile } = useAuthStore.getState()
      if (!profile || profile.role !== 'admin') {
        return { error: 'Nur Administratoren können Termine hinzufügen' }
      }

      // Validate that this date/time combination is possible
      const dayOfWeek = new Date(date + 'T00:00:00').getDay()
      const validSlots = [
        ...(dayOfWeek === 6 ? ['10:00', '12:30', '15:00', '17:30', '20:00'] : []), // Saturday
        ...(dayOfWeek === 0 ? ['10:00', '12:30', '15:00'] : []) // Sunday
      ]
      
      if (!validSlots.includes(time)) {
        return { error: 'Ungültige Zeit für diesen Wochentag' }
      }

      const { error } = await supabase
        .from('testspiele')
        .insert({
          date,
          time,
          status: 'available',
          notes: notes ?? null
        })

      if (error) throw error

      // Refresh all slots
      await get().fetchAllSlots()
      
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  removeDateTimeSlot: async (id) => {
    try {
      const { profile } = useAuthStore.getState()
      if (!profile || profile.role !== 'admin') {
        return { error: 'Nur Administratoren können Termine entfernen' }
      }

      // Only allow deletion of available slots
      const { error } = await supabase
        .from('testspiele')
        .delete()
        .eq('id', id)
        .eq('status', 'available')

      if (error) throw error

      // Refresh all slots
      await get().fetchAllSlots()
      
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },
}))
